import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './Print.module.css';
import { formatCurrency } from '../../utils/formatters';

// Constants moved outside component to reduce complexity
const FIRST_PAGE_COL_SIZE = 33;
const FIRST_PAGE_TOTAL = 66;
const EXTRA_PAGE_COL_SIZE = 49;
const EXTRA_PAGE_TOTAL = 98;

// Common PropTypes for reuse
const currencyPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

// Helper functions to extract and simplify logic
const distributeParcelas = (parcelas, totalItems, colSize) => {
  if (parcelas.length < totalItems) {
    // Divide equally if not reaching the limit
    const half = Math.ceil(parcelas.length / 2);
    return {
      col1: parcelas.slice(0, half),
      col2: parcelas.slice(half, parcelas.length)
    };
  }
  
  return {
    col1: parcelas.slice(0, colSize),
    col2: parcelas.slice(colSize, totalItems)
  };
};

const generateExtraPages = (remaining) => {
  const extraPages = [];
  
  for (let i = 0; i < remaining.length; i += EXTRA_PAGE_TOTAL) {
    const page = remaining.slice(i, i + EXTRA_PAGE_TOTAL);
    let pageData;
    
    if (page.length < EXTRA_PAGE_TOTAL) {
      // Divide equally if not reaching the extra page limit
      const half = Math.ceil(page.length / 2);
      const col1 = page.slice(0, half);
      const col2 = page.slice(half, page.length);
      
      // Fill both columns to the same size to maintain the layout
      const maxLen = Math.max(col1.length, col2.length);
      pageData = {
        col1: [...col1, ...Array(maxLen - col1.length).fill(null)],
        col2: [...col2, ...Array(maxLen - col2.length).fill(null)]
      };
    } else {
      const col1 = page.slice(0, EXTRA_PAGE_COL_SIZE);
      const col2 = page.slice(EXTRA_PAGE_COL_SIZE, EXTRA_PAGE_TOTAL);
      pageData = {
        col1,
        col2: [...col2, ...Array(EXTRA_PAGE_COL_SIZE - col2.length).fill(null)]
      };
    }
    
    extraPages.push({
      ...pageData,
      startIndex: FIRST_PAGE_TOTAL + i
    });
  }
  
  return extraPages;
};

// Extracted components to reduce cognitive complexity
const Header = ({ isMRV }) => (
  <div className={styles.printHeaderRow}>
    <img
      src={isMRV ? "img/logo_mrv.webp" : "img/logo_sensia.png"}
      alt={isMRV ? "MRV Logo" : "Sensia Logo"}
    />
    <div className={styles.headerPrint}>
      Simulação da Proposta
    </div>
  </div>
);

Header.propTypes = {
  isMRV: PropTypes.bool
};

const PropertyInfo = ({ propertyData, clientData }) => (
  <div className={styles.headerColumns}>
    <div className={styles.headerCol1}>
      <div className={styles.infoLine}>
        <strong>Empreendimento:</strong> <span>{propertyData?.selectedProperty || 'N/A'}</span>
      </div>
      <div className={styles.infoLine}>
        <strong>Unidade:</strong> <span>{propertyData?.selectedUnit || 'N/A'}</span>
      </div>
      <div className={styles.infoLine}>
        <strong>Cliente:</strong> <span>{(clientData?.buyers || [])[0]?.name || 'N/A'}</span>
      </div>
    </div>
    <div className={styles.headerCol2}>
      <div className={styles.infoLine}>
        <strong>Compra Conjunta: </strong><span>{(clientData?.buyers?.length || 0) > 1 ? 'Sim' : 'Não'}</span>
      </div>
      <div className={styles.infoLine}>
        <strong>Valor Imóvel:</strong> <span>{formatCurrency(propertyData?.propertyValue) || 'N/A'}</span>
      </div>
      <div className={styles.infoLine}>
        <strong>Tabela de Vendas:</strong> <span>{propertyData?.priceTableVersion || ''}</span>
      </div>
    </div>
  </div>
);

PropertyInfo.propTypes = {
  propertyData: PropTypes.shape({
    selectedProperty: PropTypes.string,
    selectedUnit: PropTypes.string,
    propertyValue: currencyPropType,
    priceTableVersion: PropTypes.string
  }),
  clientData: PropTypes.shape({
    buyers: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string
    }))
  })
};

const SummaryItem = ({ label, value }) => (
  <div className={styles.resumoItem}>
    <div className={styles.resumoLabel}>{label}</div>
    <div className={styles.resumoValor}>{value}</div>
  </div>
);

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node
};

const ProposalSummary = ({ simulationResult, isMRV }) => {
  if (!simulationResult) return null;
  
  const { proposta, valorProposta } = simulationResult;
  
  const carteiraValue = proposta.valorTotalNominalParcelasMensais && proposta.quantidadeParcelasMensais 
    ? `${formatCurrency(proposta.valorTotalNominalParcelasMensais)} (${proposta.quantidadeParcelasMensais}x)`
    : 'N/A';
  
  const cupomValue = proposta.cupom && proposta.cupom.valor !== undefined 
    ? formatCurrency(proposta.cupom.valor) 
    : 'N/A';
  
  return (
    <div className={styles.resumoResultados}>
      <div className={styles.tableTitle}>Resumo da Proposta</div>
      <div className={styles.resumoContainer}>
        <div className={styles.resumoLinha}>
          <SummaryItem label="Valor da Proposta" value={formatCurrency(valorProposta) || 'N/A'} />
          <SummaryItem label="Valor Financiado" value={formatCurrency(proposta.valorFinanciado) || 'N/A'} />
          <SummaryItem label="Valor do Sinal" value={formatCurrency(proposta.valorSinal) || 'N/A'} />
          <SummaryItem label={`Carteira ${isMRV ? 'MRV' : 'Sensia'}`} value={carteiraValue} />
          <SummaryItem label="Cupom" value={cupomValue} />
          <SummaryItem label="FGTS" value={proposta.fgts !== undefined ? formatCurrency(proposta.fgts) : 'N/A'} />
          <SummaryItem label="Subsídio" value={proposta.subsidio !== undefined ? formatCurrency(proposta.subsidio) : 'N/A'} />
          <SummaryItem label="Bom pagador" value={proposta.valorTotalBomPagador !== undefined ? formatCurrency(proposta.valorTotalBomPagador) : 'N/A'} />
        </div>
      </div>
    </div>
  );
};

ProposalSummary.propTypes = {
  simulationResult: PropTypes.shape({
    valorProposta: currencyPropType,
    proposta: PropTypes.shape({
      valorFinanciado: currencyPropType,
      valorSinal: currencyPropType,
      valorTotalNominalParcelasMensais: currencyPropType,
      quantidadeParcelasMensais: currencyPropType,
      cupom: PropTypes.shape({
        valor: currencyPropType
      }),
      fgts: currencyPropType,
      subsidio: currencyPropType,
      valorTotalBomPagador: currencyPropType
    })
  }),
  isMRV: PropTypes.bool
};

// Table header is consistent across tables, so extract it
const TableHeader = () => (
  <thead>
    <tr>
      <th>#</th>
      <th>Vencimento</th>
      <th>Tipo</th>
      <th>Valor Nominal (R$)</th>
    </tr>
  </thead>
);

// Component for rendering a single row
const ParcelaRow = ({ parcela, index }) => {
  if (!parcela) {
    return (
      <tr>
        <td colSpan={4} style={{ background: 'transparent', border: 'none' }}>&nbsp;</td>
      </tr>
    );
  }
  
  return (
    <tr>
      <td>{index}</td>
      <td>{parcela.dataFormatada}</td>
      <td>{parcela.tipoDescricao}</td>
      <td>{parcela.valorFormatado}</td>
    </tr>
  );
};

ParcelaRow.propTypes = {
  parcela: PropTypes.shape({
    dataFormatada: PropTypes.string,
    tipoDescricao: PropTypes.string,
    valorFormatado: PropTypes.string
  }),
  index: PropTypes.number.isRequired
};

// Component for the footer that appears on every page
const PageFooter = ({ simulationResult, currentDate, pageNumber, totalPages }) => (
  <div className={styles.footerPrint}>
    <div>
      <p>Esta simulação apresenta valores e condições meramente estimativos, sujeitos a alteração a qualquer momento. As condições válidas serão as constantes do contrato de compra e venda e, se houver, do financiamento firmado na aquisição.</p>
      <span style={{ marginLeft: 16 }}>Impresso por {simulationResult?.idCorretor || 'Usuário'} em {currentDate}</span>
      <br />
      <span>ID:{simulationResult?.proposta.id}</span>
    </div>
    <div className={styles.paginationPrint}>
      Página {pageNumber}/{totalPages}
    </div>
  </div>
);

PageFooter.propTypes = {
  simulationResult: PropTypes.shape({
    idCorretor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    proposta: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }),
  currentDate: PropTypes.string.isRequired,
  pageNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired
};

// Generate a unique key for parcela items
const getParcelaKey = (parcela, idx, prefix) => {
  // Use numero (if available) or a combination of date, type and amount to create a unique key
  if (parcela) {
    if (parcela.numero) return `${prefix}-${parcela.numero}`;
    return `${prefix}-${parcela.dataFormatada || ''}-${parcela.tipoDescricao || ''}-${idx}`;
  }
  return `${prefix}-empty-${idx}`;
};

// First page table component
const FirstPageTable = ({ col1, col2, simulationResult, currentDate, totalPagesCount }) => (
  <div className={styles.tableContainerFullPage}>
    <div className={styles.tableTitle}>Fluxo mês a mês</div>
    <div className={styles.parcelasColumnsWrapper}>
      <table className={styles.tablePrint}>
        <TableHeader />
        <tbody>
          {col1.map((parcela, idx) => (
            <ParcelaRow 
              key={getParcelaKey(parcela, idx, 'first-col1')} 
              parcela={parcela} 
              index={idx + 1} 
            />
          ))}
        </tbody>
      </table>
      
      {col2.length > 0 && (
        <table className={styles.tablePrint}>
          <TableHeader />
          <tbody>
            {col2.map((parcela, idx) => (
              <ParcelaRow 
                key={getParcelaKey(parcela, idx, 'first-col2')}
                parcela={parcela} 
                index={col1.length + idx + 1} 
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
    
    <PageFooter 
      simulationResult={simulationResult}
      currentDate={currentDate}
      pageNumber={1}
      totalPages={totalPagesCount}
    />
  </div>
);

// Extra pages table component
const ExtraPageTable = ({ page, index, simulationResult, currentDate, totalPagesCount }) => (
  <div className={styles.tableContainerFullPage}>
    <div className={styles.tableTitle}>Fluxo mês a mês (continuação)</div>
    <div className={styles.parcelasColumnsWrapper}>
      <table className={styles.tablePrint}>
        <TableHeader />
        <tbody>
          {page.col1.map((parcela, idx) => (
            <ParcelaRow 
              key={getParcelaKey(parcela, idx, `extra-${index}-col1`)}
              parcela={parcela} 
              index={page.startIndex + idx + 1} 
            />
          ))}
        </tbody>
      </table>
      
      {page.col2.some(p => p) && (
        <table className={styles.tablePrint}>
          <TableHeader />
          <tbody>
            {page.col2.map((parcela, idx) => (
              <ParcelaRow 
                key={getParcelaKey(parcela, idx, `extra-${index}-col2`)}
                parcela={parcela} 
                index={page.startIndex + page.col1.length + idx + 1} 
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
    
    <PageFooter 
      simulationResult={simulationResult}
      currentDate={currentDate}
      pageNumber={index + 2}
      totalPages={totalPagesCount}
    />
  </div>
);

const Print = ({ simulationResult, clientData, propertyData, isMRV }) => {
  const currentDate = new Date().toLocaleDateString('pt-BR');
  const printRef = useRef();

  useEffect(() => {
    // Effect to handle any initialization if needed
  }, [simulationResult]);

  const parcelas = simulationResult?.proposta?.parcelas || [];
  
  // Process first page data
  const firstPageCount = Math.min(parcelas.length, FIRST_PAGE_TOTAL);
  const firstPageData = distributeParcelas(
    parcelas.slice(0, firstPageCount),
    FIRST_PAGE_TOTAL,
    FIRST_PAGE_COL_SIZE
  );
  
  // Generate extra pages for remaining data
  const remaining = parcelas.slice(FIRST_PAGE_TOTAL);
  const extraPages = generateExtraPages(remaining);
  
  const totalPagesCount = 1 + extraPages.length;

  return (
    <div id="area-impressao" className={styles.printArea} ref={printRef}>
      <Header isMRV={isMRV} />
      
      <section id="resumo-section">
        <PropertyInfo propertyData={propertyData} clientData={clientData} />
        <ProposalSummary simulationResult={simulationResult} isMRV={isMRV} />
      </section>
      
      <div className={styles.hardPageBreak}></div>
      
      <section id="tabela-section" className={styles.pageSection}>
        {firstPageData.col1.length > 0 && (
          <FirstPageTable
            col1={firstPageData.col1}
            col2={firstPageData.col2}
            simulationResult={simulationResult}
            currentDate={currentDate}
            totalPagesCount={totalPagesCount}
          />
        )}
        
        {/* Render extra pages */}      {extraPages.map((page, pageIdx) => (
          <div className={styles.secondTableWrapper} key={`extra-page-${page.startIndex}`}>
            <ExtraPageTable
              page={page}
              index={pageIdx}
              simulationResult={simulationResult}
              currentDate={currentDate}
              totalPagesCount={totalPagesCount}
            />
          </div>
        ))}
      </section>  </div>
  );
};

// PropTypes for each extracted component
Header.propTypes = {
  isMRV: PropTypes.bool
};

PropertyInfo.propTypes = {
  propertyData: PropTypes.shape({
    selectedProperty: PropTypes.string,
    selectedUnit: PropTypes.string,
    propertyValue: currencyPropType,
    priceTableVersion: PropTypes.string
  }),
  clientData: PropTypes.shape({
    buyers: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string
    }))
  })
};

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node
};

ProposalSummary.propTypes = {
  simulationResult: PropTypes.shape({
    valorProposta: currencyPropType,
    proposta: PropTypes.shape({
      valorFinanciado: currencyPropType,
      valorSinal: currencyPropType,
      valorTotalNominalParcelasMensais: currencyPropType,
      quantidadeParcelasMensais: currencyPropType,
      cupom: PropTypes.shape({
        valor: currencyPropType
      }),
      fgts: currencyPropType,
      subsidio: currencyPropType,
      valorTotalBomPagador: currencyPropType
    })
  }),
  isMRV: PropTypes.bool
};

ParcelaRow.propTypes = {
  parcela: PropTypes.shape({
    dataFormatada: PropTypes.string,
    tipoDescricao: PropTypes.string,
    valorFormatado: PropTypes.string
  }),
  index: PropTypes.number.isRequired
};

PageFooter.propTypes = {
  simulationResult: PropTypes.shape({
    idCorretor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    proposta: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }),
  currentDate: PropTypes.string.isRequired,
  pageNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired
};

FirstPageTable.propTypes = {
  col1: PropTypes.array.isRequired,
  col2: PropTypes.array.isRequired,
  simulationResult: PropTypes.object,
  currentDate: PropTypes.string.isRequired,
  totalPagesCount: PropTypes.number.isRequired
};

ExtraPageTable.propTypes = {
  page: PropTypes.shape({
    col1: PropTypes.array.isRequired,
    col2: PropTypes.array.isRequired,
    startIndex: PropTypes.number.isRequired
  }).isRequired,
  index: PropTypes.number.isRequired,
  simulationResult: PropTypes.object,
  currentDate: PropTypes.string.isRequired,
  totalPagesCount: PropTypes.number.isRequired
};

Print.propTypes = {
  simulationResult: PropTypes.shape({
    proposta: PropTypes.shape({
      parcelas: PropTypes.array,
      valorTotal: currencyPropType,
      valorParcelas: currencyPropType,
      taxaAnual: currencyPropType,
      taxaMensal: currencyPropType,
      prazo: currencyPropType,
      entrada: currencyPropType,
      valorFinanciado: currencyPropType,
      valorSinal: currencyPropType,
      valorTotalNominalParcelasMensais: currencyPropType,
      quantidadeParcelasMensais: currencyPropType,
      cupom: PropTypes.shape({
        valor: currencyPropType
      }),
      fgts: currencyPropType,
      subsidio: currencyPropType,
      valorTotalBomPagador: currencyPropType,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    valorProposta: currencyPropType,
    idCorretor: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  clientData: PropTypes.shape({
    buyers: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string
    }))
  }),
  propertyData: PropTypes.shape({
    selectedProperty: PropTypes.string,
    selectedUnit: PropTypes.string,
    valorTotal: currencyPropType,
    propertyValue: currencyPropType,
    priceTableVersion: PropTypes.string
  }),
  isMRV: PropTypes.bool
};

Print.defaultProps = {
  isMRV: true
};

export default Print;